import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import BlogSidebar from '@/components/BlogSidebar';
import BlogCTA from '@/components/BlogCTA';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, ArrowRight, User, Phone, FileText } from 'lucide-react';
import { getAllPosts, getPostsByCategory, BlogPost } from '@/data/blogPosts';

const BlogPage = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');

  const allPosts = categoryFilter 
    ? getPostsByCategory(categoryFilter, language)
    : getAllPosts();

  // Sort by date
  const posts = allPosts.sort((a, b) => 
    new Date(b.published_date).getTime() - new Date(a.published_date).getTime()
  );

  const featuredPost = posts[0];
  const otherPosts = posts.slice(1);

  const seoTitle = language === 'en'
    ? 'Landscaping & Sod Delivery Tips in Texas | The Grass Depot Blog'
    : 'Guías y Consejos de Pasto y Jardinería en Texas | Blog The Grass Depot';

  const seoDescription = language === 'en'
    ? 'Expert advice on sod, mulch, soil, sand and landscaping materials for Texas homeowners. Tips from Montgomery County\'s trusted landscaping supplier.'
    : 'Consejos expertos sobre pasto, mulch, tierra, arena y materiales de jardinería para propietarios de Texas. Consejos del proveedor de confianza de Montgomery County.';

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": language === 'en' ? "The Grass Depot Blog" : "Blog The Grass Depot",
    "description": seoDescription,
    "url": `https://thegrassdepotllc.com/${language}/blog`,
    "publisher": {
      "@type": "Organization",
      "name": "The Grass Depot LLC",
      "logo": {
        "@type": "ImageObject",
        "url": "https://thegrassdepotllc.com/grass-depot-logo.png"
      }
    }
  };

  const getPostData = (post: BlogPost) => ({
    slug: language === 'en' ? post.slug_en : post.slug_es,
    title: language === 'en' ? post.title_en : post.title_es,
    excerpt: language === 'en' ? post.excerpt_en : post.excerpt_es,
    category: language === 'en' ? post.category_en : post.category_es,
  });

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        canonical={`/${language}/blog`}
        structuredData={structuredData}
        language={language}
      />
      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary via-primary/90 to-primary/80 py-20 overflow-hidden">
          <div className="absolute inset-0 bg-[url('/grass-depot-storefront.png')] bg-cover bg-center opacity-10" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/80 to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl">
              <Badge className="mb-4 bg-white/20 text-white border-white/30">
                {language === 'en' ? 'Expert Landscaping Tips' : 'Consejos de Paisajismo'}
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {language === 'en' 
                  ? 'Landscaping & Sod Delivery Tips in Texas' 
                  : 'Guías y Consejos de Pasto y Jardinería en Texas'
                }
              </h1>
              <p className="text-xl text-white/90 mb-8 max-w-2xl">
                {language === 'en'
                  ? 'Expert advice on sod, mulch, soil, sand and landscaping materials from Montgomery County\'s trusted supplier.'
                  : 'Consejos expertos sobre pasto, mulch, tierra, arena y materiales de jardinería del proveedor de confianza de Montgomery County.'
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`}>
                  <Button size="lg" variant="secondary" className="w-full sm:w-auto">
                    <FileText className="mr-2 h-5 w-5" />
                    {language === 'en' ? 'Get a Free Quote' : 'Cotiza Gratis'}
                  </Button>
                </Link>
                <a href="tel:+19363200763">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white/95 border-white text-primary font-semibold hover:bg-white hover:text-primary">
                    <Phone className="mr-2 h-5 w-5" />
                    (936) 320-0763
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Content */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-3">
                {categoryFilter && (
                  <div className="mb-6 flex items-center gap-4">
                    <Badge variant="secondary" className="text-sm py-1 px-3">
                      {language === 'en' ? 'Category: ' : 'Categoría: '}{categoryFilter}
                    </Badge>
                    <Link to={`/${language}/blog`} className="text-sm text-primary hover:underline font-medium">
                      {language === 'en' ? '✕ Clear filter' : '✕ Limpiar filtro'}
                    </Link>
                  </div>
                )}

                {/* Featured Post */}
                {featuredPost && (
                  <Card className="mb-10 overflow-hidden group hover:shadow-xl transition-all duration-300 border-0 bg-background">
                    <div className="md:flex">
                      <div className="md:w-1/2 relative overflow-hidden">
                        <img 
                          src={featuredPost.featured_image}
                          alt={getPostData(featuredPost).title}
                          className="w-full h-64 md:h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4">
                          <Badge className="bg-primary text-primary-foreground shadow-lg">
                            {language === 'en' ? '⭐ Featured' : '⭐ Destacado'}
                          </Badge>
                        </div>
                      </div>
                      <div className="md:w-1/2 p-6 md:p-8 flex flex-col justify-center">
                        <Badge variant="outline" className="w-fit mb-4">
                          {getPostData(featuredPost).category}
                        </Badge>
                        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4 group-hover:text-primary transition-colors leading-tight">
                          <Link to={`/${language}/blog/${getPostData(featuredPost).slug}`}>
                            {getPostData(featuredPost).title}
                          </Link>
                        </h2>
                        <p className="text-muted-foreground mb-6 line-clamp-3 text-base">
                          {getPostData(featuredPost).excerpt}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                          <span className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {featuredPost.author}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(featuredPost.published_date).toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {featuredPost.read_time}
                          </span>
                        </div>
                        <Link to={`/${language}/blog/${getPostData(featuredPost).slug}`}>
                          <Button size="lg" className="group/btn">
                            {language === 'en' ? 'Read Article' : 'Leer Artículo'}
                            <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Section Title */}
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-foreground">
                    {language === 'en' ? 'Latest Articles' : 'Últimos Artículos'}
                  </h2>
                  <p className="text-muted-foreground mt-1">
                    {language === 'en' 
                      ? `${otherPosts.length} articles to help you with your landscaping projects`
                      : `${otherPosts.length} artículos para ayudarte con tus proyectos de paisajismo`
                    }
                  </p>
                </div>

                {/* Posts Grid */}
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {otherPosts.map((post, index) => {
                    const postData = getPostData(post);
                    return (
                      <Card key={index} className="group overflow-hidden hover:shadow-xl transition-all duration-300 border-0 bg-background flex flex-col">
                        <div className="relative overflow-hidden">
                          <img 
                            src={post.featured_image}
                            alt={postData.title}
                            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute top-3 left-3">
                            <Badge variant="secondary" className="bg-white/95 backdrop-blur-sm text-xs text-primary font-semibold shadow-md">
                              {postData.category}
                            </Badge>
                          </div>
                          <div className="absolute top-3 right-3">
                            <Badge variant="secondary" className="bg-white/95 backdrop-blur-sm text-xs text-primary font-semibold shadow-md">
                              <Clock className="h-3 w-3 mr-1" />
                              {post.read_time}
                            </Badge>
                          </div>
                        </div>
                        <CardContent className="p-5 flex-1 flex flex-col">
                          <h3 className="text-lg font-bold text-foreground mb-3 group-hover:text-primary transition-colors line-clamp-2 leading-snug">
                            <Link to={`/${language}/blog/${postData.slug}`}>
                              {postData.title}
                            </Link>
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-4 flex-1">
                            {postData.excerpt}
                          </p>
                          <div className="flex items-center justify-between pt-4 border-t">
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              {new Date(post.published_date).toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES', {
                                month: 'short',
                                day: 'numeric'
                              })}
                            </div>
                            <Link to={`/${language}/blog/${postData.slug}`}>
                              <Button variant="ghost" size="sm" className="group/btn text-primary hover:text-primary">
                                {language === 'en' ? 'Read' : 'Leer'}
                                <ArrowRight className="ml-1 h-3 w-3 group-hover/btn:translate-x-1 transition-transform" />
                              </Button>
                            </Link>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Bottom CTA */}
                <div className="mt-12">
                  <BlogCTA variant="full" />
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <BlogSidebar currentCategory={categoryFilter || undefined} />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPage;
